import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * usePushSubscription — registers the device for Web Push notifications.
 *
 * [2026-06-11] This was the missing link in the push chain: the backend
 * (/api/push/subscribe, VAPID send) and the service worker `push` handler
 * both existed, but no frontend code ever called pushManager.subscribe(),
 * so no device was ever actually subscribed.
 *
 * Behavior:
 *   • Runs once per app load, and re-runs when the user logs in/out.
 *   • Asks Notification permission only for LOGGED-IN users (avoids
 *     spamming anonymous visitors with a permission prompt).
 *   • Sends the user's Bearer token with the subscription so the backend
 *     can link the device to the user for chat-reply pushes.
 *   • Safe no-op on unsupported browsers (no SW / no PushManager / iOS
 *     Safari not installed as PWA).
 */
export function usePushSubscription() {
  const { user, token } = useAuth();
  const attempted = useRef(false);

  useEffect(() => {
    // Only subscribe authenticated users; permission prompts for anonymous
    // visitors hurt UX and the chat push needs a user_id anyway.
    if (!user || !token) return;
    if (attempted.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (!('Notification' in window)) return;

    attempted.current = true;

    (async () => {
      try {
        // 1) Permission (no-op if already granted; bail if denied)
        let permission = Notification.permission;
        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') return;

        // 2) VAPID public key from backend
        const keyRes = await fetch(`${API_URL}/api/push/vapid-public-key`);
        if (!keyRes.ok) return; // push not configured server-side
        const { publicKey } = await keyRes.json();
        if (!publicKey) return;

        // 3) Subscribe via the already-registered service worker
        const reg = await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          });
        }

        // 4) Send to backend with user_id (links device → user for chat push)
        const deviceId = localStorage.getItem('pricemap_device_id') || '';
        const subJson = sub.toJSON();
        // The backend links the device to the user from the Bearer token;
        // a user_id in the body is ignored (anyone could send one).
        await fetch(`${API_URL}/api/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            endpoint: subJson.endpoint,
            keys: subJson.keys,
            device_id: deviceId,
          }),
        });
      } catch (err) {
        // Never break the app over push — log and move on.
        console.warn('[Push] subscription failed:', err);
      }
    })();
  }, [user, token]);
}

/** Convert base64url VAPID key to Uint8Array for pushManager.subscribe. */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
